import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   Analytics.js — redesigned with light theme matching Profile.js
   rgb(28,115,186) primary, white cards, Inter font
───────────────────────────────────────────────────────────────────────────── */

const B = '#1c73ba';
const BL = '#e8f3fb';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@600,700&display=swap');
:root{--blue:#1c73ba;--bl:#e8f3fb;--bd:#155fa0;--text:#111827;--muted:#6b7280;--border:#e5e7eb;--bg:#f3f4f6;--red:#ef4444;--green:#10b981;--gold:#f59e0b}
*{box-sizing:border-box;margin:0;padding:0}
.an-root{min-height:100vh;background:var(--bg);font-family:'Inter',system-ui,sans-serif;color:var(--text);-webkit-font-smoothing:antialiased}
.an-nav{position:sticky;top:0;z-index:50;background:#fff;border-bottom:1px solid var(--border);padding:0 24px;height:56px;display:flex;align-items:center;gap:12px}
.an-logo{font-family:'Clash Display',sans-serif;font-size:1.2rem;font-weight:700;color:var(--blue);text-decoration:none;letter-spacing:-0.3px}
.an-logo span{color:var(--gold)}
.an-back{display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border:1px solid var(--border);border-radius:8px;background:transparent;color:var(--muted);cursor:pointer;font-size:13px;font-family:inherit;transition:all .15s}
.an-back:hover{border-color:var(--blue);color:var(--blue)}
.an-body{max-width:920px;margin:0 auto;padding:24px 20px}
.an-header{display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:24px}
.an-title{font-family:'Clash Display',sans-serif;font-size:1.5rem;font-weight:700;color:var(--text);margin-bottom:4px}
.an-sub{font-size:13px;color:var(--muted)}
.an-period{display:flex;gap:4px}
.an-pd{padding:6px 14px;border-radius:99px;border:1px solid var(--border);background:#fff;font-size:13px;font-weight:500;color:var(--muted);cursor:pointer;transition:all .15s;font-family:inherit}
.an-pd:hover{border-color:var(--blue);color:var(--blue)}
.an-pd.sel{background:var(--bl);border-color:var(--blue);color:var(--blue);font-weight:700}
.an-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:20px}
.an-card{background:#fff;border:1px solid var(--border);border-radius:12px;padding:16px 18px;position:relative;overflow:hidden}
.an-card-accent{position:absolute;top:0;left:0;right:0;height:3px;border-radius:12px 12px 0 0}
.an-card-label{font-size:10px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:1px;margin-bottom:8px}
.an-card-value{font-size:28px;font-weight:700;color:var(--text);letter-spacing:-1px;line-height:1}
.an-card-sub{font-size:11px;color:var(--muted);margin-top:4px}
.an-section{background:#fff;border:1px solid var(--border);border-radius:12px;padding:18px 20px;margin-bottom:14px}
.an-section-title{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:var(--blue);margin-bottom:16px}
.an-row{display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--border)}
.an-row:last-child{border-bottom:none}
.an-row-main{font-size:13px;font-weight:600;color:var(--text);margin-bottom:2px}
.an-row-sub{font-size:11px;color:var(--muted)}
.an-row-val{font-size:18px;font-weight:700;color:var(--blue)}
.an-two{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}
@media(max-width:580px){.an-two{grid-template-columns:1fr}}
.an-bar-wrap{display:flex;align-items:flex-end;gap:3px;height:60px}
.an-bar-col{flex:1;display:flex;flex-direction:column;align-items:center;gap:2px}
.an-bar{width:100%;border-radius:3px 3px 0 0;min-height:4px;transition:height .4s}
.an-date-row{display:flex;justify-content:space-between;margin-top:4px}
.an-date{font-size:10px;color:var(--muted)}
.an-empty{font-size:13px;color:#d1d5db;padding:12px 0;font-style:italic}
.an-chip{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:99px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px}
.an-rank{width:22px;height:22px;border-radius:6px;background:var(--bg);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:var(--muted);flex-shrink:0}
.an-spinner{width:18px;height:18px;border:2px solid var(--bl);border-top-color:var(--blue);border-radius:50%;animation:spin .7s linear infinite;display:inline-block}
.an-center{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:var(--bg);font-family:'Inter',sans-serif}
@keyframes spin{to{transform:rotate(360deg)}}
.an-dev-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.an-dev-row{display:flex;align-items:center;gap:8px;margin-bottom:6px}
`;

const EV_LABEL = {
  profile_view:'Profile Views', accessory_tap:'NFC Taps', link_click:'Link Clicks',
  contact_save:'Contact Saves', contact_exchange:'Exchanges', chat_click:'Chat Clicks',
};
const DEV_COLOR = { mobile:B, desktop:'#7c3aed', tablet:'#10b981', unknown:'#9ca3af' };
const STAT_ACCENT = [B, '#10b981', '#7c3aed', '#f59e0b', '#db2777'];

// SVG back arrow
const BackArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);

function SparkBar({ data }) {
  if (!data?.length) return <div className="an-empty">No data for this period</div>;
  const max = Math.max(...data.map(d => d.views), 1);
  return (
    <>
      <div className="an-bar-wrap">
        {data.map((d, i) => (
          <div key={i} className="an-bar-col">
            <div className="an-bar"
              style={{ height: `${Math.max(4, (d.views / max) * 52)}px`, background: `rgba(28,115,186,${0.25 + 0.75 * (d.views / max)})` }}
              title={`${d.date}: ${d.views}`}/>
          </div>
        ))}
      </div>
      {data.length > 1 && (
        <div className="an-date-row">
          <span className="an-date">{data[0]?.date}</span>
          <span className="an-date">{data[data.length - 1]?.date}</span>
        </div>
      )}
    </>
  );
}

function DonutChart({ slices }) {
  if (!slices.length) return <div className="an-empty">No device data</div>;
  const r = 26, circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <svg width={64} height={64} viewBox="0 0 64 64" style={{ flexShrink: 0 }}>
        <circle r={r} cx={32} cy={32} fill="none" stroke="#f3f4f6" strokeWidth={9}/>
        {slices.map((s, i) => {
          const dash = s.pct * circ;
          const el = (
            <circle key={i} r={r} cx={32} cy={32} fill="none"
              stroke={s.colour} strokeWidth={9}
              strokeDasharray={`${dash} ${circ}`}
              strokeDashoffset={-offset * circ}
              style={{ transform: 'rotate(-90deg)', transformOrigin: '32px 32px', transition: 'stroke-dasharray .5s' }}/>
          );
          offset += s.pct;
          return el;
        })}
      </svg>
      <div style={{ flex: 1 }}>
        {slices.map(s => (
          <div key={s.device} className="an-dev-row">
            <div className="an-dev-dot" style={{ background: s.colour }}/>
            <span style={{ fontSize: 12, color: '#6b7280', textTransform: 'capitalize', flex: 1 }}>{s.device}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#111' }}>{Math.round(s.pct * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const navigate = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [days,    setDays]    = useState(30);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/analytics/my?days=${days}`)
      .then(({ data }) => setStats(data))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="an-center">
      <style>{CSS}</style>
      <div className="an-spinner"/>
      <span style={{ fontSize: 13, color: '#6b7280' }}>Loading analytics…</span>
    </div>
  );

  if (error) return (
    <div className="an-center">
      <style>{CSS}</style>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>Could not load analytics</div>
      <div style={{ fontSize: 13, color: '#9ca3af' }}>{error}</div>
      <button className="an-back" onClick={() => navigate('/profile')}><BackArrow/> Back to profile</button>
    </div>
  );

  const s   = stats?.summary || {};
  const tl  = stats?.topLinks || [];
  const dt  = stats?.dailyTrend || [];
  const dev = stats?.devices || [];
  const at  = stats?.accessoryTaps || [];
  const bt  = stats?.byType || [];

  const totalDevViews = dev.reduce((a, d) => a + d.count, 0) || 1;
  let devOff = 0;
  const devSlices = dev.map(d => {
    const pct = d.count / totalDevViews;
    const sl = { ...d, pct, colour: DEV_COLOR[d.device] || '#9ca3af' };
    devOff += pct;
    return sl;
  });

  const statCards = [
    { label: 'Profile Views',   value: s.totalViews,     sub: `${s.uniqueVisitors ?? 0} unique visitors`, accent: STAT_ACCENT[0] },
    { label: 'NFC Taps',        value: s.totalTaps,      sub: 'accessory taps',                           accent: STAT_ACCENT[1] },
    { label: 'Link Clicks',     value: s.totalClicks,    sub: 'across all links',                         accent: STAT_ACCENT[2] },
    { label: 'Contact Saves',   value: s.totalSaves,     sub: '.vcf downloads',                           accent: STAT_ACCENT[3] },
    { label: 'Exchanges',       value: s.totalExchanges, sub: 'contact shares',                           accent: STAT_ACCENT[4] },
  ];

  return (
    <div className="an-root">
      <style>{CSS}</style>

      <nav className="an-nav">
        <button className="an-back" onClick={() => navigate('/profile')}><BackArrow/> Profile</button>
        <a href="/" className="an-logo">flap<span>.</span></a>
      </nav>

      <div className="an-body">
        <div className="an-header">
          <div>
            <div className="an-title">Analytics</div>
            <div className="an-sub">
              {stats?.flapid && <span style={{ color: B }}>/{stats.flapid} · </span>}
              {stats?.period}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div className="an-period">
              {[7, 30, 90].map(d => (
                <button key={d} className={`an-pd ${days === d ? 'sel' : ''}`} onClick={() => setDays(d)}>{d}d</button>
              ))}
            </div>
            <button className="an-pd" onClick={load} title="Refresh"><RefreshIcon/></button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="an-grid">
          {statCards.map(c => (
            <div key={c.label} className="an-card">
              <div className="an-card-accent" style={{ background: c.accent }}/>
              <div className="an-card-label">{c.label}</div>
              <div className="an-card-value" style={{ color: c.accent }}>{(c.value ?? 0).toLocaleString()}</div>
              {c.sub && <div className="an-card-sub">{c.sub}</div>}
            </div>
          ))}
        </div>

        {/* Daily trend */}
        <div className="an-section">
          <div className="an-section-title">Daily trend — {days} days</div>
          <SparkBar data={dt}/>
        </div>

        {/* Events + Devices */}
        <div className="an-two">
          <div className="an-section" style={{ marginBottom: 0 }}>
            <div className="an-section-title">All events</div>
            {bt.length ? bt.map(e => (
              <div key={e.eventType} className="an-row">
                <div>
                  <div className="an-row-main">{EV_LABEL[e.eventType] || e.eventType}</div>
                  <div className="an-row-sub">{e.uniqueIps} unique IPs</div>
                </div>
                <div className="an-row-val">{e.total}</div>
              </div>
            )) : <div className="an-empty">No events yet</div>}
          </div>

          <div className="an-section" style={{ marginBottom: 0 }}>
            <div className="an-section-title">Devices</div>
            <DonutChart slices={devSlices}/>
          </div>
        </div>

        {/* Top links */}
        {tl.length > 0 && (
          <div className="an-section">
            <div className="an-section-title">Top clicked links</div>
            {tl.map((l, i) => (
              <div key={i} className="an-row" style={{ gap: 12 }}>
                <div className="an-rank">{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="an-row-main" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.label || l.platform || (l.url ? l.url.slice(0, 40) : '—')}
                    {l.type && <span className="an-chip" style={{ marginLeft: 6, background: BL, color: B }}>{l.type}</span>}
                  </div>
                  <div className="an-row-sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.url}</div>
                </div>
                <div className="an-row-val">{l.count}</div>
              </div>
            ))}
          </div>
        )}

        {/* Accessory taps */}
        {at.length > 0 && (
          <div className="an-section">
            <div className="an-section-title">Accessory tap breakdown</div>
            {at.map((a, i) => (
              <div key={i} className="an-row">
                <div>
                  <div className="an-row-main" style={{ fontFamily: 'monospace', fontSize: 12 }}>{a.accessoryFlapid || '—'}</div>
                  <div className="an-row-sub">
                    Last tapped {a.lastTapped ? new Date(a.lastTapped).toLocaleDateString() : '—'}
                    {a.deviceTypes?.length ? ` · ${a.deviceTypes.join(', ')}` : ''}
                  </div>
                </div>
                <div className="an-row-val" style={{ color: '#10b981' }}>{a.count}</div>
              </div>
            ))}
          </div>
        )}

        {/* Empty */}
        {!s.totalViews && !s.totalTaps && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: '#9ca3af' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1" style={{ marginBottom: 16 }}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 8 }}>No data yet</div>
            <div style={{ fontSize: 13 }}>Share your profile link or tap your card to start collecting analytics.</div>
          </div>
        )}
      </div>
    </div>
  );
}